import React, { useState } from 'react';
import { EmailBlock, FooterBlockContent } from '../../types';
import { Plus, X } from 'lucide-react';

interface FooterPropertiesProps {
  block: EmailBlock;
  onUpdate: (updates: Partial<FooterBlockContent>) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
  isDarkMode: boolean;
}

const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' }
] as const;

export const FooterProperties: React.FC<FooterPropertiesProps> = ({
  block,
  onUpdate,
  isDarkMode
}) => {
  const content = block.content as FooterBlockContent;
  const [newSocialPlatform, setNewSocialPlatform] = useState<string>('facebook');
  const [newSocialUrl, setNewSocialUrl] = useState<string>('');

  const handleAddSocial = () => {
    if (!newSocialUrl.trim()) return;
    const socialLinks = [...(content.socialLinks || []), {
      platform: newSocialPlatform as any,
      url: newSocialUrl
    }];
    onUpdate({ socialLinks });
    setNewSocialUrl('');
  };

  const handleRemoveSocial = (index: number) => {
    const socialLinks = content.socialLinks?.filter((_, i) => i !== index) || [];
    onUpdate({ socialLinks });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Background Color
        </label>
        <input
          type="color"
          value={content.backgroundColor || '#f3f4f6'}
          onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Text Color
        </label>
        <input
          type="color"
          value={content.textColor || '#6b7280'}
          onChange={(e) => onUpdate({ textColor: e.target.value })}
          className="w-full h-10 border rounded-md"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Font Size
          </label>
          <input
            type="number"
            value={content.fontSize || 12}
            onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 12 })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            min="10"
            max="16"
          />
        </div>

        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Padding
          </label>
          <input
            type="text"
            value={content.padding || '30px 20px'}
            onChange={(e) => onUpdate({ padding: e.target.value })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="30px 20px"
          />
        </div>
      </div>

      {/* Company Info */}
      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Company Information
        </label>
        <textarea
          value={content.companyInfo || ''}
          onChange={(e) => onUpdate({ companyInfo: e.target.value })}
          className={`
            w-full px-3 py-2 border rounded-md resize-none
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
          rows={3}
          placeholder="Company address, contact info, etc."
        />
      </div>

      {/* Social Links */}
      <div>
        <label className={`
          block text-sm font-medium mb-2
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Social Media Links
        </label>
        
        {content.socialLinks && content.socialLinks.length > 0 && (
          <div className="space-y-2 mb-2">
            {content.socialLinks.map((social, index) => (
              <div key={index} className={`
                flex items-center gap-2 p-2 rounded
                ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}
              `}>
                <span className={`
                  text-sm flex-1
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                `}>
                  {SOCIAL_PLATFORMS.find(p => p.value === social.platform)?.label}
                </span>
                <input
                  type="url"
                  value={social.url}
                  onChange={(e) => {
                    const socialLinks = [...(content.socialLinks || [])];
                    socialLinks[index] = { ...socialLinks[index], url: e.target.value };
                    onUpdate({ socialLinks });
                  }}
                  className={`
                    flex-1 px-2 py-1 text-sm border rounded
                    ${isDarkMode 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder="https://..."
                />
                <button
                  onClick={() => handleRemoveSocial(index)}
                  className={`
                    p-1 rounded hover:bg-opacity-80
                    ${isDarkMode ? 'text-error-400 hover:bg-gray-600' : 'text-error-600 hover:bg-gray-200'}
                  `}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <select
            value={newSocialPlatform}
            onChange={(e) => setNewSocialPlatform(e.target.value)}
            className={`
              px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          >
            {SOCIAL_PLATFORMS.map(platform => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={newSocialUrl}
            onChange={(e) => setNewSocialUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddSocial()}
            className={`
              flex-1 px-3 py-2 border rounded-md text-sm
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="URL"
          />
          <button
            onClick={handleAddSocial}
            className={`
              px-3 py-2 rounded-md
              ${isDarkMode 
                ? 'bg-accent-600 hover:bg-accent-700 text-white' 
                : 'bg-accent-500 hover:bg-accent-600 text-white'
              }
            `}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Subscription Management */}
      <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className={`
          text-sm font-medium
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Subscription Management
        </h4>

        <div className="space-y-2">
          <label className={`
            flex items-center gap-2
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            <input
              type="checkbox"
              checked={content.showUnsubscribeLink !== false}
              onChange={(e) => onUpdate({ showUnsubscribeLink: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Show Unsubscribe Link</span>
          </label>

          {content.showUnsubscribeLink !== false && (
            <div className="ml-6">
              <label className={`
                block text-xs mb-1
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Unsubscribe Link Text
              </label>
              <input
                type="text"
                value={content.unsubscribeText || 'Unsubscribe'}
                onChange={(e) => onUpdate({ unsubscribeText: e.target.value })}
                className={`
                  w-full px-3 py-2 border rounded-md text-sm
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
              />
            </div>
          )}

          <label className={`
            flex items-center gap-2
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            <input
              type="checkbox"
              checked={content.showPreferenceCenter !== false}
              onChange={(e) => onUpdate({ showPreferenceCenter: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Show Preference Center Link</span>
          </label>

          {content.showPreferenceCenter !== false && (
            <div className="ml-6">
              <label className={`
                block text-xs mb-1
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Preference Center Link Text
              </label>
              <input
                type="text"
                value={content.preferenceCenterText || 'Manage Preferences'}
                onChange={(e) => onUpdate({ preferenceCenterText: e.target.value })}
                className={`
                  w-full px-3 py-2 border rounded-md text-sm
                  ${isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
              />
            </div>
          )}
        </div>
      </div>

      {/* Copyright */}
      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Copyright Text
        </label>
        <input
          type="text"
          value={content.copyrightText || ''}
          onChange={(e) => onUpdate({ copyrightText: e.target.value })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
          placeholder="© 2024 Company Name. All rights reserved."
        />
      </div>
    </div>
  );
};
